import { Lock, Unlock, ListPlus } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { LyricsAssist } from "@/components/creator/LyricsAssist";
import LyricsCoWriter from "@/components/creator/LyricsCoWriter";

function structureSkeleton(tags) {
  return tags.map((t) => `[${t}]\n\n`).join("").trimEnd() + "\n";
}

/** Lyrics editor with Lyrics Lock (exact submission + automatic listen-back check). */
export default function LyricsField({ form, structure, maxChars, genreLabel, instrumental, onChange, onBusyChange, busy }) {
  const lock = form.lyrics_lock;
  const len = (form.lyrics || "").length;
  const over = len > maxChars;
  const tags = structure?.tags || [];
  return (
    <section data-testid="lyrics-field">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
        <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-white/70">Lyrics {instrumental ? "· optional idea" : ""}</span>
        {!instrumental ? (
          <label className={`flex cursor-pointer items-center gap-2 rounded-full border px-3 py-1.5 transition-colors ${lock ? "border-emerald-400/40 bg-emerald-500/10" : "border-white/10 bg-white/[0.03]"}`}>
            {lock ? <Lock className="h-3.5 w-3.5 text-emerald-300" /> : <Unlock className="h-3.5 w-3.5 text-white/50" />}
            <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-white/80">Lyrics Lock</span>
            <Switch data-testid="lyrics-lock-toggle" checked={lock} onCheckedChange={(v) => onChange({ lyrics_lock: v })} />
          </label>
        ) : null}
      </div>
      <textarea
        data-testid="song-lyrics-input"
        value={form.lyrics}
        onChange={(e) => onChange({ lyrics: e.target.value })}
        readOnly={busy}
        aria-busy={busy}
        rows={12}
        spellCheck={false}
        placeholder={instrumental ? "Describe the feeling of the music (optional)" : "[Verse 1]\nWrite or paste your lyrics here…\n\n[Chorus]\n…"}
        className={`w-full rounded-2xl border bg-black/40 px-4 py-3.5 font-body text-[15px] leading-relaxed text-white placeholder:text-white/30 focus:outline-none ${over ? "border-rose-400/70" : "border-white/10 focus:border-blue-400/60"}`}
      />
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <p data-testid="lyrics-lock-note" className="text-[12px] leading-relaxed text-white/50">
          {instrumental ? "Instrumental — no words will be sung."
            : lock ? "Locked — your words are sent exactly as typed. ELPM listens back to every take and warns you if a line was changed."
              : "Unlocked — short text is treated as an idea and ELPM writes the words."}
        </p>
        <span data-testid="lyrics-char-count" className={`font-mono text-[10px] uppercase tracking-wider ${over ? "text-rose-300" : "text-white/35"}`}>{len} / {maxChars}</span>
      </div>
      {tags.length && !instrumental ? (
        <button
          type="button"
          data-testid="lyrics-insert-structure"
          disabled={Boolean((form.lyrics || "").trim())}
          onClick={() => onChange({ lyrics: structureSkeleton(tags) })}
          title={(form.lyrics || "").trim() ? "Clear the lyrics box to insert the section template" : undefined}
          className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-white/60 transition-colors hover:text-white disabled:opacity-40"
        >
          <ListPlus className="h-3.5 w-3.5" /> Insert {structure.label} tags
        </button>
      ) : null}
      {!instrumental && !lock ? (
        <div className="mt-4">
          <LyricsAssist title={form.title} styleLabel={genreLabel} lyrics={form.lyrics} onLyrics={(lyrics) => onChange({ lyrics })} disabled={busy} onBusyChange={onBusyChange} />
        </div>
      ) : null}
      {!instrumental ? <LyricsCoWriter title={form.title} styleLabel={genreLabel} lyrics={form.lyrics || ""} onLyrics={(lyrics) => onChange({ lyrics })} disabled={busy} /> : null}
    </section>
  );
}
